/**
 * Tenant-side Plan card — rendered in Organization Settings → General.
 *
 * Sections:
 *   1. Current-plan summary with usage meters and a click-triggered info
 *      popover listing every feature unlocked on the current plan (sourced
 *      from the entitlements API — never hardcoded).
 *   2. Upgrade plan section — one card per upgrade-eligible tier, with
 *      monthly + yearly price and a "What's new" list showing only the
 *      features and limit upgrades introduced relative to the
 *      immediate-lower tier (set diff against `TIER_ORDER[idx-1]`, NOT
 *      against the user's current plan). Each card also shows a "Plus
 *      everything in {lowerTier}" footer so the transitive scope is
 *      explicit.
 *   3. Pending-request banner (when status = PENDING).
 *
 * Both `admin` and `owner` can request a plan change; the BFF
 * enforces the real gate (RolesGuard hierarchy: admin ≥ supervisor ≥
 * agent ≥ light_agent).
 */

import { useMemo, useState } from 'react';
import { Button, Card, InfoClickPopover, Spinner } from '@centraoneplus/ui';
import { diffPlan, LimitDelta, PlanDiff } from './diffPlan';
import {
  EffectivePriceRow,
  FeatureLabel,
  LimitLabel,
  PlanChangeRequest,
  PlanTier,
  TierCatalog,
  useCancelPlanChangeRequest,
  useCancelSubscription,
  useCurrentPlanChangeRequest,
  useEntitlements,
  usePublicPricing,
  useTenantSubscription,
  useTenantUsage,
} from './hooks';
import { PlanChangeRequestModal } from './PlanChangeRequestModal';
import { PlanHistory } from './PlanHistory';
import { TrialBanner } from './TrialBanner';
import { UsageMeter } from './UsageMeter';
import { usePlanWebSocketSync } from './usePlanWebSocketSync';

// Same semantics as `formatLimitValue` in @centraoneplus/shared, mirrored
// here so the web app doesn't pull from the backend-only shared package.
// `-1` is the canonical sentinel for "unlimited" everywhere on the
// platform; everything else gets locale-grouped digits.
function formatLimitValue(value: number): string {
  if (value === -1) return 'Unlimited';
  return new Intl.NumberFormat().format(value);
}

function formatLimitWithUnit(value: number, unit: string): string {
  const formatted = formatLimitValue(value);
  if (!unit) return formatted;
  // Per-period suffixes ("/month", "/minute") attach with no separator;
  // unit nouns ("GB") attach with a space. The catalog leading-slash
  // convention disambiguates the two so this stays a one-liner.
  return unit.startsWith('/') ? `${formatted}${unit}` : `${formatted} ${unit}`;
}

const TIER_ORDER: PlanTier[] = ['free', 'growth', 'professional'];
const TIER_LABELS: Record<PlanTier, string> = {
  free: 'Free',
  growth: 'Growth',
  professional: 'Professional',
};

// Roles allowed to request a plan change. Mirrors the BFF RolesGuard
// hierarchy on POST /subscriptions/tenant/:tenantId/plan-change-request
// (which uses @Roles('admin') — admin or higher passes).
const PLAN_CHANGE_ROLES = new Set(['admin', 'owner']);

interface Props {
  tenantId: string;
  userRole: string; // 'owner' | 'admin' | 'supervisor' | 'agent' | ...
}

export default function PlanCard({ tenantId, userRole }: Props) {
  usePlanWebSocketSync(tenantId);
  const canChangePlan = PLAN_CHANGE_ROLES.has(userRole);
  const isOwner = userRole === 'owner';
  const entitlements = useEntitlements(tenantId);
  const pricing = usePublicPricing();
  const currentRequest = useCurrentPlanChangeRequest(tenantId);
  const cancelRequest = useCancelPlanChangeRequest(tenantId);
  const usage = useTenantUsage(tenantId);
  const subscription = useTenantSubscription(tenantId);
  const cancelSubscription = useCancelSubscription(subscription.data?.id ?? null);

  const [modalTarget, setModalTarget] = useState<PlanTier | null>(null);

  if (entitlements.isLoading) {
    return (
      <Card className="p-6">
        <Spinner />
      </Card>
    );
  }

  const snapshot = entitlements.data;
  if (!snapshot) {
    return (
      <Card className="p-6 text-sm text-gray-500 dark:text-gray-400">
        Unable to load plan information. Please refresh.
      </Card>
    );
  }

  const currentIdx = TIER_ORDER.indexOf(snapshot.plan);
  // Every tier strictly above the current one is upgrade-eligible. We
  // intersect this with whatever tiers the BFF actually exposes so a
  // missing PlanPricing row (e.g. SuperAdmin hasn't priced Professional
  // in the caller's currency yet) just hides that card instead of 500ing.
  const upgradeTiers: PlanTier[] = TIER_ORDER.slice(currentIdx + 1);
  const lowerTier = currentIdx > 0 ? TIER_ORDER[currentIdx - 1] : null;

  const pending = currentRequest.data;

  async function handleCancelSubscription() {
    if (!subscription.data) return;
    const isPaid = (subscription.data.plan ?? 'free') !== 'free';
    const msg = isPaid
      ? 'Cancel your subscription? You will keep access until the end of your current billing period; after that, your workspace drops to the Free tier. Are you sure?'
      : 'Cancel your subscription? Your workspace will drop to the Free tier.';
    if (!confirm(msg)) return;
    try {
      await cancelSubscription.mutateAsync({ immediate: false });
    } catch (e) {
      /* toast handled by mutation error state */
    }
  }

  return (
    <div className="space-y-4">
      <TrialBanner
        status={subscription.data?.status ?? 'active'}
        trialEndsAt={subscription.data?.trialEndsAt}
        fallbackPlan={subscription.data?.trialFallbackPlan}
      />

      <PendingRequestBanner
        request={pending ?? null}
        canCancel={canChangePlan}
        onCancel={async (id) => {
          if (!confirm('Cancel this pending plan-change request?')) return;
          try {
            await cancelRequest.mutateAsync(id);
          } catch (e) {
            // non-fatal — query invalidation happens on next refresh
          }
        }}
      />

      {subscription.data?.status === 'cancelled' && (
        <Card className="p-4 bg-gray-50 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 text-sm">
          Your subscription is scheduled to end
          {subscription.data.currentPeriodEnd
            ? ` on ${new Date(subscription.data.currentPeriodEnd).toLocaleDateString()}`
            : ' at the end of the current period'}
          . After that, your workspace will drop to the Free tier.
        </Card>
      )}

      {subscription.data?.status === 'past_due' && (
        <Card className="p-4 bg-amber-50 border-amber-200 text-amber-900 text-sm">
          <div className="font-semibold">Payment past due</div>
          <p className="mt-1">
            Your billing is past due. Please update your payment method to avoid
            service suspension
            {subscription.data.gracePeriodEndsAt
              ? ` on ${new Date(subscription.data.gracePeriodEndsAt).toLocaleDateString()}`
              : ' at the end of the grace period'}
            .
          </p>
        </Card>
      )}

      <CurrentPlanCard
        plan={snapshot.plan}
        featureLabels={snapshot.featureLabels}
        limits={snapshot.limits}
        usage={usage.data}
      />

      {upgradeTiers.length > 0 && (
        <UpgradePlansSection
          upgradeTiers={upgradeTiers}
          pricing={pricing.data ?? null}
          pricingLoading={pricing.isLoading}
          disabled={!canChangePlan || Boolean(pending)}
          disabledReason={
            !canChangePlan
              ? 'Only an organization admin or owner can request a plan change.'
              : pending
                ? 'You already have a pending plan-change request.'
                : undefined
          }
          onRequestUpgrade={(tier) => setModalTarget(tier)}
        />
      )}

      {lowerTier && (
        <DowngradeCard
          tier={lowerTier}
          disabled={!canChangePlan || Boolean(pending)}
          onRequestDowngrade={() => setModalTarget(lowerTier)}
        />
      )}

      {isOwner &&
        snapshot.plan !== 'free' &&
        subscription.data?.status !== 'cancelled' && (
          <Card className="p-4 text-sm text-gray-700 dark:text-gray-200 flex items-center justify-between">
            <div>
              <span className="font-medium">Cancel subscription</span>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Keeps you on your current plan until the end of the billing period,
                then drops your workspace to Free.
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleCancelSubscription}
              disabled={cancelSubscription.isPending}
            >
              {cancelSubscription.isPending ? 'Cancelling…' : 'Cancel subscription'}
            </Button>
          </Card>
        )}

      <PlanHistory tenantId={tenantId} />

      {modalTarget && (
        <PlanChangeRequestModal
          tenantId={tenantId}
          fromPlan={snapshot.plan}
          toPlan={modalTarget}
          priceRow={pricing.data?.prices.find((p) => p.tier === modalTarget) ?? null}
          taxMode={pricing.data?.taxMode ?? 'disabled'}
          annualDiscountPercent={pricing.data?.annualDiscountPercent ?? 0}
          onClose={() => setModalTarget(null)}
        />
      )}
    </div>
  );
}

function PendingRequestBanner({
  request,
  canCancel,
  onCancel,
}: {
  request: PlanChangeRequest | null;
  canCancel: boolean;
  onCancel: (id: string) => Promise<void>;
}) {
  if (!request || request.status !== 'PENDING') return null;
  return (
    <Card className="p-4 bg-amber-50 border-amber-200 text-amber-900 flex items-start justify-between gap-4">
      <div>
        <div className="font-semibold">
          Plan change pending: {TIER_LABELS[request.fromPlan]} → {TIER_LABELS[request.toPlan]}
        </div>
        <p className="text-sm mt-1">
          Your request is awaiting review by the CentraOne platform team. We'll
          notify you when it's approved.
        </p>
      </div>
      {canCancel && (
        <Button
          variant="outline"
          onClick={() => onCancel(request.id)}
          className="shrink-0"
        >
          Cancel request
        </Button>
      )}
    </Card>
  );
}

function CurrentPlanCard({
  plan,
  featureLabels,
  limits,
  usage,
}: {
  plan: PlanTier;
  featureLabels: FeatureLabel[];
  limits: Record<string, number>;
  usage: {
    agents: number;
    ticketsThisMonth: number;
    activeAutomationRules: number;
    customFields: number;
    kbArticles: number;
  } | undefined;
}) {
  const featureCount = featureLabels.length;
  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Current plan</div>
          <div className="text-2xl font-semibold capitalize">{TIER_LABELS[plan]}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
            <span>
              {featureCount} feature{featureCount === 1 ? '' : 's'} unlocked
            </span>
            {featureCount > 0 && (
              <InfoClickPopover
                iconSize="sm"
                placement="bottom"
                ariaLabel="View full feature list"
                content={<FeatureListContent labels={featureLabels} />}
              />
            )}
          </div>
        </div>
        <span className="inline-block px-2 py-1 text-xs rounded bg-emerald-100 text-emerald-800">
          Active
        </span>
      </div>

      {usage && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 pt-2 border-t border-gray-100 dark:border-gray-700">
          <UsageMeter
            label="Agents"
            usage={usage.agents < 0 ? 0 : usage.agents}
            limit={limits.agents ?? 0}
          />
          <UsageMeter
            label="Tickets this month"
            usage={usage.ticketsThisMonth < 0 ? 0 : usage.ticketsThisMonth}
            limit={limits.ticketsPerMonth ?? 0}
          />
          <UsageMeter
            label="Automation rules"
            usage={usage.activeAutomationRules < 0 ? 0 : usage.activeAutomationRules}
            limit={limits.automationRules ?? 0}
          />
          <UsageMeter
            label="Custom fields"
            usage={usage.customFields < 0 ? 0 : usage.customFields}
            limit={limits.customFields ?? 0}
          />
          <UsageMeter
            label="KB articles"
            usage={usage.kbArticles < 0 ? 0 : usage.kbArticles}
            limit={limits.kbArticles ?? 0}
          />
        </div>
      )}
    </Card>
  );
}

// Renders the full feature list grouped by category. Used inside the
// InfoClickPopover on the current-plan card and on each upgrade-plan card.
// Color tokens match the surrounding themed panel (white in light, gray-800
// in dark) — never the legacy dark-tooltip palette.
function FeatureListContent({ labels }: { labels: FeatureLabel[] }) {
  const grouped = useMemo(() => {
    const out = new Map<string, FeatureLabel[]>();
    for (const label of labels) {
      const arr = out.get(label.category) ?? [];
      arr.push(label);
      out.set(label.category, arr);
    }
    return Array.from(out.entries());
  }, [labels]);

  return (
    <div
      className="text-left text-xs space-y-2 max-h-[480px] overflow-y-auto pr-1"
      style={{ width: 320 }}
    >
      {grouped.map(([category, items]) => (
        <div key={category}>
          <div className="font-semibold text-gray-900 dark:text-gray-100 mb-0.5">
            {category}
          </div>
          <ul className="space-y-0.5">
            {items.map((item) => (
              <li key={item.id} className="text-gray-600 dark:text-gray-400">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {item.name}
                </span>
                <span> — {item.description}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function UpgradePlansSection({
  upgradeTiers,
  pricing,
  pricingLoading,
  disabled,
  disabledReason,
  onRequestUpgrade,
}: {
  upgradeTiers: PlanTier[];
  pricing: {
    prices: EffectivePriceRow[];
    tiers: TierCatalog[];
    limitLabels: Record<string, LimitLabel>;
    annualDiscountPercent: number;
  } | null;
  pricingLoading: boolean;
  disabled: boolean;
  disabledReason?: string;
  onRequestUpgrade: (tier: PlanTier) => void;
}) {
  if (pricingLoading) {
    return (
      <Card className="p-6">
        <Spinner />
      </Card>
    );
  }
  if (!pricing) {
    return (
      <Card className="p-4 text-sm text-gray-500 dark:text-gray-400">
        Unable to load upgrade options. Please refresh.
      </Card>
    );
  }

  // Build the list of cards to render — only tiers that have a price
  // configured AND a tier catalog entry. Either being missing means the
  // tier is not yet upgrade-ready and we hide the card rather than show
  // a half-broken UI. Both holes are surfaced to SuperAdmin via /admin/pricing.
  // Each card computes its own diff vs. the *immediate* lower tier in
  // TIER_ORDER (NOT vs. the user's current plan) so a Free user looking
  // at the Professional card still sees Professional-vs-Growth deltas;
  // the page also renders the Growth card alongside, completing the
  // upgrade path.
  const cards = upgradeTiers
    .map((tier) => {
      const idx = TIER_ORDER.indexOf(tier);
      const lowerTierName = idx > 0 ? TIER_ORDER[idx - 1] : null;
      return {
        tier,
        priceRow: pricing.prices.find((p) => p.tier === tier) ?? null,
        catalog: pricing.tiers.find((t) => t.tier === tier) ?? null,
        lowerTier: lowerTierName,
        lowerCatalog: lowerTierName
          ? pricing.tiers.find((t) => t.tier === lowerTierName) ?? null
          : null,
      };
    })
    .filter((c) => c.priceRow !== null);

  if (cards.length === 0) {
    return (
      <Card className="p-4 text-sm text-gray-500 dark:text-gray-400">
        No upgrade plans are currently available. Contact our team if you
        expected to see additional tiers here.
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Upgrade your plan
        </h3>
        {pricing.annualDiscountPercent > 0 && (
          <span className="text-xs text-emerald-700 font-medium">
            Save {pricing.annualDiscountPercent}% with yearly billing
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cards.map(({ tier, priceRow, catalog, lowerTier, lowerCatalog }) => (
          <UpgradePlanCard
            key={tier}
            tier={tier}
            priceRow={priceRow!}
            catalog={catalog}
            lowerTier={lowerTier}
            lowerCatalog={lowerCatalog}
            limitLabels={pricing.limitLabels ?? {}}
            annualDiscountPercent={pricing.annualDiscountPercent}
            disabled={disabled}
            disabledReason={disabledReason}
            onRequestUpgrade={() => onRequestUpgrade(tier)}
          />
        ))}
      </div>
    </div>
  );
}

function UpgradePlanCard({
  tier,
  priceRow,
  catalog,
  lowerTier,
  lowerCatalog,
  limitLabels,
  annualDiscountPercent,
  disabled,
  disabledReason,
  onRequestUpgrade,
}: {
  tier: PlanTier;
  priceRow: EffectivePriceRow;
  catalog: TierCatalog | null;
  lowerTier: PlanTier | null;
  lowerCatalog: TierCatalog | null;
  limitLabels: Record<string, LimitLabel>;
  annualDiscountPercent: number;
  disabled: boolean;
  disabledReason?: string;
  onRequestUpgrade: () => void;
}) {
  // Compute the "what's new" delta vs. the immediate-lower tier. This
  // is the single source of truth for both the highlight preview and
  // the InfoClickPopover full list — both views are filtered to "only
  // what this plan adds" so the user is never re-shown features they
  // already have on the tier below.
  const diff: PlanDiff = useMemo(
    () =>
      diffPlan(catalog, lowerCatalog, limitLabels, (msg) =>
        // Catalog data anomaly (limit downgrade between tiers) — surface
        // to console so SuperAdmin can chase it via /admin/pricing.
        // Plan & Billing has no other logging convention; no toast.
        // eslint-disable-next-line no-console
        console.warn(msg),
      ),
    [catalog, lowerCatalog, limitLabels],
  );

  const totalDeltaCount = diff.newFeatures.length + diff.limitUpgrades.length;
  // Highlight 5 items, mixing limit upgrades first (they're usually the
  // most concrete "why upgrade" signal — "100 → 1,000 tickets/mo" beats
  // a feature name) then features. If a tier introduces fewer than 5
  // new items, show what there is.
  const limitHighlights = diff.limitUpgrades.slice(0, 5);
  const featureHighlights = diff.newFeatures.slice(
    0,
    Math.max(0, 5 - limitHighlights.length),
  );
  const moreCount = Math.max(
    0,
    totalDeltaCount - limitHighlights.length - featureHighlights.length,
  );

  const lowerTierLabel = lowerTier ? TIER_LABELS[lowerTier] : null;

  return (
    <Card className="p-6 space-y-3 border-blue-200 flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm text-blue-700 font-medium">Upgrade to</div>
          <div className="text-xl font-semibold capitalize flex items-center gap-1">
            <span>{TIER_LABELS[tier]}</span>
            {totalDeltaCount > 0 && (
              <InfoClickPopover
                iconSize="sm"
                placement="right"
                ariaLabel={`View what's new in ${TIER_LABELS[tier]}`}
                content={
                  <UpgradeDiffContent
                    tierLabel={TIER_LABELS[tier]}
                    diff={diff}
                    lowerTierLabel={lowerTierLabel}
                  />
                }
              />
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold">
            {priceRow.currency} {priceRow.monthlyAmount}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">per month</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            or {priceRow.currency} {priceRow.annualAmount}/year
            {annualDiscountPercent > 0 && (
              <span className="ml-1 text-emerald-700">
                (save {annualDiscountPercent}%)
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-2">
        <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
          What's new in {TIER_LABELS[tier]}
        </div>
        {totalDeltaCount === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Same features and limits as {lowerTierLabel ?? 'the lower tier'} at
            this price.
          </p>
        ) : (
          <ul className="text-sm text-gray-700 dark:text-gray-200 space-y-1 list-disc pl-5">
            {limitHighlights.map((d) => (
              <li key={`limit:${d.key}`}>
                <LimitDeltaText delta={d} />
              </li>
            ))}
            {featureHighlights.map((f) => (
              <li key={`feat:${f.id}`} title={f.description}>
                {f.name}
              </li>
            ))}
            {moreCount > 0 && (
              <li className="list-none text-xs text-gray-500 dark:text-gray-400 pl-0">
                + {moreCount} more — click the info icon for the full list
              </li>
            )}
          </ul>
        )}
        {lowerTierLabel && (
          <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
            Plus everything in <strong>{lowerTierLabel}</strong>.
          </p>
        )}
      </div>

      <div className="pt-2">
        <Button
          onClick={onRequestUpgrade}
          disabled={disabled}
          title={disabledReason}
        >
          Request upgrade
        </Button>
        {disabled && disabledReason && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{disabledReason}</p>
        )}
      </div>
    </Card>
  );
}

// One-line render for a limit upgrade. Shape:
//   - "Tickets — 100 → Unlimited / month"   (increase, with /period unit)
//   - "Storage — 1 GB → 10 GB"               (increase, with noun unit)
//   - "Now available: Automation rules — 10" (unlock from 0)
function LimitDeltaText({ delta }: { delta: LimitDelta }) {
  if (delta.kind === 'unlock') {
    return (
      <span>
        Now available: <span className="font-medium">{delta.name}</span>
        {' — '}
        {formatLimitWithUnit(delta.toValue, delta.unit)}
      </span>
    );
  }
  const fromText = formatLimitWithUnit(delta.fromValue, delta.unit);
  const toText = formatLimitWithUnit(delta.toValue, delta.unit);
  return (
    <span>
      <span className="font-medium">{delta.name}</span>
      {' — '}
      {fromText} → {toText}
    </span>
  );
}

// Full "what's new" surface inside the InfoClickPopover. Mirrors the
// preview list shape but uncapped and with feature category groupings.
// Renders only categories that contain at least one new item, plus the
// limit upgrades section when present, and a "Plus everything in {lower}"
// footer at the bottom of the scroll content.
function UpgradeDiffContent({
  tierLabel,
  diff,
  lowerTierLabel,
}: {
  tierLabel: string;
  diff: PlanDiff;
  lowerTierLabel: string | null;
}) {
  const groupedFeatures = useMemo(() => {
    const out = new Map<string, FeatureLabel[]>();
    for (const label of diff.newFeatures) {
      const arr = out.get(label.category) ?? [];
      arr.push(label);
      out.set(label.category, arr);
    }
    return Array.from(out.entries());
  }, [diff.newFeatures]);

  const hasAny =
    diff.limitUpgrades.length > 0 || diff.newFeatures.length > 0;

  return (
    <div
      className="text-left text-xs space-y-2 max-h-[480px] overflow-y-auto pr-1"
      style={{ width: 320 }}
    >
      <div className="font-semibold text-gray-900 dark:text-gray-100">
        What's new in {tierLabel}
      </div>

      {!hasAny && (
        <p className="text-gray-600 dark:text-gray-400">
          No additional features or limit upgrades vs.{' '}
          {lowerTierLabel ?? 'the lower tier'}.
        </p>
      )}

      {diff.limitUpgrades.length > 0 && (
        <div>
          <div className="font-semibold text-gray-900 dark:text-gray-100 mb-0.5">
            Higher limits
          </div>
          <ul className="space-y-0.5">
            {diff.limitUpgrades.map((d) => (
              <li
                key={`limit:${d.key}`}
                className="text-gray-600 dark:text-gray-400"
              >
                <LimitDeltaText delta={d} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {groupedFeatures.map(([category, items]) => (
        <div key={category}>
          <div className="font-semibold text-gray-900 dark:text-gray-100 mb-0.5">
            {category}
          </div>
          <ul className="space-y-0.5">
            {items.map((item) => (
              <li key={item.id} className="text-gray-600 dark:text-gray-400">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {item.name}
                </span>
                <span> — {item.description}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {lowerTierLabel && (
        <div className="pt-1 text-gray-600 dark:text-gray-400">
          Plus everything in{' '}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {lowerTierLabel}
          </span>
          .
        </div>
      )}
    </div>
  );
}

function DowngradeCard({
  tier,
  disabled,
  onRequestDowngrade,
}: {
  tier: PlanTier;
  disabled: boolean;
  onRequestDowngrade: () => void;
}) {
  return (
    <Card className="p-4 text-sm text-gray-700 dark:text-gray-200 flex items-center justify-between">
      <div>
        Need a smaller plan? You can request a downgrade to <strong>{TIER_LABELS[tier]}</strong>.
      </div>
      <Button variant="outline" onClick={onRequestDowngrade} disabled={disabled}>
        Request downgrade
      </Button>
    </Card>
  );
}

